// imports
import { useParams } from 'react-router-dom';
import { Box, Grid, Tab, Tabs, Typography } from '@mui/material';
import { FC, Fragment, useCallback, useEffect, useState } from 'react';

//components
import TabPanel from 'components/common/TabPanel';
import Invoice from 'components/main/Settings/Invoice';
import EditUserForm from 'components/main/Users/View/EditUserForm';
import OrgStaffTable from 'components/main/Users/View/OrgStaffTable';
import CurrentPlan from 'components/main/Settings/PaymentMethods/CurrentPlan';

//constants, interfaces, graphql
import { ParamType } from 'interfaces';
import { BillingIcon, PaymentIcon, ProfileIcon, UsersIcon } from 'assets/svgs';
import { UserPayload, useGetOrgAdminLazyQuery } from 'generated/graphql';
import {
  EDIT_PROFILE_TEXT,
  HTTP_STATUS,
  INVOICES_TEXT,
  STAFF_TEXT,
  SUBSCRIPTION_TEXT,
  USER_DETAILS_TEXT,
  UserDetailTabs,
} from 'constants/index';

const ViewUser: FC = () => {
  const { id } = useParams<ParamType>();
  const [user, setUser] = useState<UserPayload['user']>(null);
  const [value, setValue] = useState(UserDetailTabs.EDIT_INFO);

  const handleChange = (_: React.SyntheticEvent, newValue: UserDetailTabs) => {
    setValue(newValue);
  };

  const [getOrgAdmin, { loading }] = useGetOrgAdminLazyQuery({
    onCompleted: (data) => {
      const { getOrgAdmin } = data || {};
      const { response, user } = getOrgAdmin || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setUser(user as UserPayload['user']);
      }
    },
    onError: () => {
      setUser(null);
    },
  });

  const fetchUser = useCallback(async () => {
    await getOrgAdmin({
      variables: {
        getUserInput: { id: id ?? '' },
      },
    });
  }, [getOrgAdmin, id]);

  useEffect(() => {
    id && fetchUser();
  }, [fetchUser, id]);

  return (
    <Fragment>
      <Box mb={2}>
        <Typography variant="h5" mb={1}>
          {USER_DETAILS_TEXT}
        </Typography>
        <Tabs value={value} onChange={handleChange} aria-label="staff details tabs">
          <Tab
            value={UserDetailTabs.EDIT_INFO}
            label={EDIT_PROFILE_TEXT}
            icon={ProfileIcon}
            iconPosition="start"
          />

          <Tab
            value={UserDetailTabs.SUBSCRIPTION}
            label={SUBSCRIPTION_TEXT}
            icon={PaymentIcon}
            wrapped
            iconPosition="start"
          />
          <Tab
            value={UserDetailTabs.INVOICES}
            label={INVOICES_TEXT}
            icon={BillingIcon}
            iconPosition="start"
          />
          <Tab
            value={UserDetailTabs.STAFF}
            label={STAFF_TEXT}
            icon={UsersIcon}
            iconPosition="start"
          />
        </Tabs>
        <TabPanel value={UserDetailTabs.EDIT_INFO} activeTab={value}>
          <EditUserForm loading={loading} id={id} user={user} />
        </TabPanel>
        <TabPanel value={UserDetailTabs.SUBSCRIPTION} activeTab={value}>
          <Grid container>
            <Grid item xs={12} md={8}>
              <CurrentPlan isOrg />
            </Grid>
          </Grid>
        </TabPanel>
        <TabPanel value={UserDetailTabs.INVOICES} activeTab={value}>
          <Invoice isOrg />
        </TabPanel>
        <TabPanel value={UserDetailTabs.STAFF} activeTab={value}>
          <OrgStaffTable organizationId={user?.organization?.id} />
        </TabPanel>
      </Box>
    </Fragment>
  );
};

export default ViewUser;
